import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
  createFilterOptions,
} from '@mui/material';

import ScopeSelect from './ScopeSelect';
import { Filters, Model, quarterLabel } from './presentationModel';

type Props = {
  filters: Filters;
  options: Model['options'];
  view: string;
  onChange: (patch: Partial<Filters>) => void;
  onReset: () => void;
};

/** Report controls only. The host continues to own navigation and shell. */
export default function CommercialKpiFilters({ filters, options, view, onChange, onReset }: Props) {
  const choose = (key: keyof Filters, label: string, values: Array<string>) => {
    if (values.length > 200) {
      const choices = Array.from(new Set(['All', filters[key], ...values]));
      return (
        <Autocomplete
          key={key}
          size="small"
          options={choices}
          value={filters[key]}
          filterOptions={createFilterOptions<string>({ limit: 100 })}
          onChange={(_, value) => onChange({ [key]: value ?? 'All' })}
          renderInput={(props) => <TextField {...props} label={label} placeholder="Search names" />}
        />
      );
    }
    return (
      <ScopeSelect
        key={key}
        label={label}
        value={filters[key]}
        values={['All', ...values]}
        onChange={(value) => onChange({ [key]: value })}
      />
    );
  };
  const heading = (label: string) => (
    <Typography variant="subtitle2" color="primary.main" sx={{ pt: 0.5, fontSize: 12, fontWeight: 600 }}>
      {label}
    </Typography>
  );
  return (
    <Box
      component="section"
      aria-label="Global Filters"
      sx={{
        p: 1,
        height: 'auto',
        alignSelf: 'start',
        minHeight: 0,
        overflow: 'visible',
        '& .MuiInputBase-root': { fontSize: 13 },
        '& .MuiInputLabel-root': { fontSize: 13 },
      }}
    >
      <Stack spacing={1.1}>
        <Typography variant="h6" sx={{ fontSize: 16 }}>
          Global Filters
        </Typography>
        {heading('Date')}
        <TextField
          select
          size="small"
          fullWidth
          label="Quarter Selection"
          value={filters.quarter}
          onChange={(event) => onChange({ quarter: event.target.value, selection: '[]' })}
        >
          {Array.from(new Set([filters.quarter, ...options.quarters])).map((quarter) => (
            <MenuItem key={quarter} value={quarter}>
              {quarterLabel(quarter)}
            </MenuItem>
          ))}
        </TextField>
        <Divider />
        {view === 'Agent Hub' ? (
          // Agent Hub BDM Tracker filters: Bdm Name Selection, Product Name, Brand Name, Offer Country Name.
          <>
            {heading('User')}
            {choose('bdm', 'Bdm Name Selection', options.bdms)}
            <Divider />
            {heading('Product')}
            {choose('product', 'Product Name', options.products)}
            {choose('brand', 'Brand Name', options.brands)}
            <Divider />
            {heading('Location')}
            {choose('country', 'Offer Country Name', options.countries)}
          </>
        ) : (
          <>
            {heading('User')}
            {view !== 'MM' && choose('manager', 'Rsm', options.managers)}
            {choose('person', 'Pcm Name', options.people)}
            <Divider />
            {heading('Location')}
            {choose('region', 'Pcm Region', options.regions)}
          </>
        )}
        {(view === 'PCM' || view === 'Experience') && (
          <Accordion
            disableGutters
            elevation={0}
            defaultExpanded={['category', 'brand', 'product', 'opportunity', 'offerName', 'rsmRegion', 'country'].some(
              (field) => filters[field as keyof Filters] !== 'All',
            )}
            sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">Advanced filters</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={1.5}>
                {heading('Product')}
                {choose('category', 'Category', options.categories)}
                {choose('brand', 'Brand Name', options.brands)}
                {choose('product', 'Product Name', options.products)}
                {choose('opportunity', 'Offer Opportunity Name', options.opportunities)}
                {choose('offerName', 'Offer Name', options.offerNames)}
                {heading('Location')}
                {choose('rsmRegion', 'Rsm Region', options.rsmRegions)}
                {choose('country', 'Offer Country Name', options.countries)}
              </Stack>
            </AccordionDetails>
          </Accordion>
        )}
        <Box>
          <Button size="small" onClick={onReset}>
            Reset global filters
          </Button>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          One value per global filter. Lists come from the live query period; search long name lists to narrow the first
          100 matches. Use Ctrl, ⌘ or Shift on progress rows for multiple exact owner/region selections.
        </Typography>
      </Stack>
    </Box>
  );
}
